import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Modal, Button, Text } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import "./add-patient.css";

const AddPatient = () => {
  const navigate = useNavigate();
  const [patientData, setPatientData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    gender: "",
    email: "",
    dateOfBirth: { month: "", date: "", year: "" },
    homeAddress: "",
  });

  const [patientErrors, setPatientErrors] = useState({});
  const [patientTouched, setPatientTouched] = useState({
    firstName: false,
    lastName: false,
    phone: false,
    gender: false,
    email: false,
    month: false,
    date: false,
    year: false,
    homeAddress: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [modalOpened, setModalOpened] = useState(false);

  const API_ENDPOINT = "/wp-json/dbsb/v1/patients";

  const handlePatientChange = (e) => {
    const { name, value } = e.target;
    updatePatientForm(name, value);
  };

  const handlePatientBlur = (e) => {
    const { name } = e.target;
    setPatientTouched((prev) => ({ ...prev, [name]: true }));
    validatePatient();
  };

  const handlePatientPhoneChange = (value) => {
    updatePatientForm("phone", value || "");
  };

  const handlePatientPhoneBlur = () => {
    setPatientTouched((prev) => ({ ...prev, phone: true }));
    validatePatient();
  };

  const handlePatientDateChange = (field, value) => {
    let sanitizedValue = value;

    if (field === "date") {
      sanitizedValue = value.slice(0, 2);
      if (
        sanitizedValue === "" ||
        (Number(sanitizedValue) >= 0 && !isNaN(Number(sanitizedValue)))
      ) {
        updatePatientForm("dateOfBirth", {
          ...patientData.dateOfBirth,
          [field]: sanitizedValue,
        });
      }
    } else if (field === "year") {
      sanitizedValue = value.slice(0, 4);
      if (
        sanitizedValue === "" ||
        (Number(sanitizedValue) >= 0 && !isNaN(Number(sanitizedValue)))
      ) {
        updatePatientForm("dateOfBirth", {
          ...patientData.dateOfBirth,
          [field]: sanitizedValue,
        });
      }
    } else {
      updatePatientForm("dateOfBirth", {
        ...patientData.dateOfBirth,
        [field]: sanitizedValue,
      });
    }
  };

  const handlePatientDateBlur = (field) => {
    setPatientTouched((prev) => ({ ...prev, [field]: true }));
    validatePatient();
  };

  const updatePatientForm = (name, value) => {
    if (name === "dateOfBirth") {
      setPatientData((prev) => ({
        ...prev,
        dateOfBirth: value,
      }));
    } else {
      setPatientData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validatePatient = () => {
    const newErrors = {};
    const touched = patientTouched;

    if (touched.firstName && !patientData.firstName)
      newErrors.firstName = "First Name is required";
    if (touched.lastName && !patientData.lastName)
      newErrors.lastName = "Last Name is required";
    if (touched.gender && !patientData.gender)
      newErrors.gender = "Gender is required";

    if (touched.phone) {
      if (!patientData.phone) {
        newErrors.phone = "Phone number is required";
      } else {
        const phoneNum = patientData.phone.replace(/\D/g, "");
        if (phoneNum.length < 10 || phoneNum.length > 15) {
          newErrors.phone = "Please enter a valid phone number";
        }
      }
    }

    if (touched.email) {
      if (!patientData.email) {
        newErrors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(patientData.email)) {
        newErrors.email = "Please enter a valid email address";
      }
    }

    const { month, date, year } = patientData.dateOfBirth;
    const currentYear = new Date().getFullYear();

    if (touched.month && !month) {
      newErrors.month = "Month is required";
    }

    if (touched.date) {
      if (!date) {
        newErrors.date = "Day is required";
      } else {
        const dateNum = Number(date);
        if (isNaN(dateNum) || dateNum < 1 || dateNum > 31) {
          newErrors.date = "Day is invalid";
        }
      }
    }

    if (touched.year) {
      if (!year) {
        newErrors.year = "Year is required";
      } else if (year.length === 4) {
        const yearNum = Number(year);
        if (isNaN(yearNum) || yearNum < 1900 || yearNum > currentYear) {
          newErrors.year = "Year is invalid";
        }
      }
    }

    if (touched.homeAddress && !patientData.homeAddress)
      newErrors.homeAddress = "Home Address is required";

    setPatientErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const isPatientIncomplete = () => {
    const { dateOfBirth, ...otherFields } = patientData;
    return (
      Object.values(otherFields).some((value) => value === "") ||
      Object.values(dateOfBirth).some((value) => value === "")
    );
  };

  useEffect(() => {
    validatePatient();
  }, [patientData, patientTouched]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPatientTouched({
      firstName: true,
      lastName: true,
      phone: true,
      gender: true,
      email: true,
      month: true,
      date: true,
      year: true,
      homeAddress: true,
    });

    if (!validatePatient()) return;

    setIsLoading(true);
    setApiError(null);

    try {
      const dobFormatted = `${patientData.dateOfBirth.year}-${String(
        patientData.dateOfBirth.month
      ).padStart(2, "0")}-${String(patientData.dateOfBirth.date).padStart(
        2,
        "0"
      )}`;

      // Check if we have the nonce
      if (!window.wpApiSettings?.nonce) {
        throw new Error("Authentication token not found. Please try logging in again.");
      }

      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": window.wpApiSettings.nonce,
        },
        credentials: "same-origin",
        body: JSON.stringify({
          first_name: patientData.firstName,
          last_name: patientData.lastName,
          email: patientData.email,
          phone: patientData.phone,
          gender: patientData.gender,
          dob: dobFormatted,
          address: patientData.homeAddress,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle different error status codes
        switch (response.status) {
          case 401:
            throw new Error("You must be logged in to add patients. Please log in and try again.");
          case 403:
            throw new Error("You don't have permission to add patients. Only healthcare providers can add patients.");
          default:
            throw new Error(data.message || "Failed to add patient. Please try again.");
        }
      }

      setModalOpened(true);
      setPatientData({
        firstName: "",
        lastName: "",
        phone: "",
        gender: "",
        email: "",
        dateOfBirth: { month: "", date: "", year: "" },
        homeAddress: "",
      });
      setPatientTouched({
        firstName: false,
        lastName: false,
        phone: false,
        gender: false,
        email: false,
        month: false,
        date: false,
        year: false,
        homeAddress: false,
      });
    } catch (error) {
      setApiError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const formVariants = {
    hidden: { opacity: 0, x: -100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut", staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: [0.6, -0.05, 0.01, 0.99] },
    },
  };

  const buttonVariants = {
    hover: {
      scale: 1.02,
      backgroundColor: "#d1d9e6",
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.98 },
  };

  return (
    <div className="add-patient-container">
      <motion.div
        className="add-patient-content"
        variants={formVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.h2 variants={itemVariants}>Add patient</motion.h2>
        <motion.div className="add-patient-card" variants={itemVariants}>
          <motion.form
            onSubmit={handleSubmit}
            className="add-patient-form"
            variants={formVariants}
          >
            <motion.div variants={itemVariants} className="form-group name-row">
              <div className="name-input">
                <label>First Name</label>
                <input
                  name="firstName"
                  value={patientData.firstName}
                  onChange={handlePatientChange}
                  onBlur={handlePatientBlur}
                  placeholder="Enter first name"
                />
                {patientErrors.firstName && (
                  <span className="error">{patientErrors.firstName}</span>
                )}
              </div>
              <div className="name-input">
                <label>Last Name</label>
                <input
                  name="lastName"
                  value={patientData.lastName}
                  onChange={handlePatientChange}
                  onBlur={handlePatientBlur}
                  placeholder="Enter last name"
                />
                {patientErrors.lastName && (
                  <span className="error">{patientErrors.lastName}</span>
                )}
              </div>
            </motion.div>
            <motion.div variants={itemVariants} className="form-group phone">
              <label>Phone Number</label>
              <PhoneInput
                international
                countryCallingCodeEditable={false}
                defaultCountry="NG"
                value={patientData.phone}
                onChange={handlePatientPhoneChange}
                onBlur={handlePatientPhoneBlur}
                placeholder="Enter phone number"
                maxLength={17}
              />
              {patientErrors.phone && (
                <span className="error">{patientErrors.phone}</span>
              )}
            </motion.div>
            <motion.div variants={itemVariants} className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={patientData.email}
                onChange={handlePatientChange}
                onBlur={handlePatientBlur}
                placeholder="Enter your email address"
              />
              {patientErrors.email && (
                <span className="error">{patientErrors.email}</span>
              )}
            </motion.div>
            <motion.div variants={itemVariants} className="form-group">
              <label>Gender</label>
              <select
                name="gender"
                value={patientData.gender}
                onChange={handlePatientChange}
                onBlur={handlePatientBlur}
              >
                <option value="">Select from dropdown</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
              {patientErrors.gender && (
                <span className="error">{patientErrors.gender}</span>
              )}
            </motion.div>
            <motion.div variants={itemVariants} className="form-group dob">
              <label>Date of Birth</label>
              <div className="dob-inputs">
                <div className="dob-input">
                  <select
                    name="month"
                    value={patientData.dateOfBirth.month}
                    onChange={(e) =>
                      handlePatientDateChange("month", e.target.value)
                    }
                    onBlur={() => handlePatientDateBlur("month")}
                  >
                    <option value="">Month</option>
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {new Date(0, i).toLocaleString("default", {
                          month: "long",
                        })}
                      </option>
                    ))}
                  </select>
                  {patientErrors.month && (
                    <span className="error">{patientErrors.month}</span>
                  )}
                </div>
                <div className="dob-input">
                  <input
                    type="text"
                    name="date"
                    value={patientData.dateOfBirth.date}
                    onChange={(e) =>
                      handlePatientDateChange("date", e.target.value)
                    }
                    onBlur={() => handlePatientDateBlur("date")}
                    placeholder="Day"
                    maxLength="2"
                    style={{
                      WebkitAppearance: "none",
                      MozAppearance: "textfield",
                    }}
                  />
                  {patientErrors.date && (
                    <span className="error">{patientErrors.date}</span>
                  )}
                </div>
                <div className="dob-input">
                  <input
                    type="text"
                    name="year"
                    value={patientData.dateOfBirth.year}
                    onChange={(e) =>
                      handlePatientDateChange("year", e.target.value)
                    }
                    onBlur={() => handlePatientDateBlur("year")}
                    placeholder="Year"
                    maxLength="4"
                    style={{
                      WebkitAppearance: "none",
                      MozAppearance: "textfield",
                    }}
                  />
                  {patientErrors.year && (
                    <span className="error">{patientErrors.year}</span>
                  )}
                </div>
              </div>
            </motion.div>
            <motion.div variants={itemVariants} className="form-group">
              <label>Home Address</label>
              <textarea
                name="homeAddress"
                value={patientData.homeAddress}
                onChange={handlePatientChange}
                onBlur={handlePatientBlur}
                placeholder="Enter your home address"
                rows="4"
              />
              {patientErrors.homeAddress && (
                <span className="error">{patientErrors.homeAddress}</span>
              )}
            </motion.div>
          </motion.form>
        </motion.div>
        {apiError && (
          <motion.p className="error-message" variants={itemVariants}>
            {apiError}
          </motion.p>
        )}
        <motion.div variants={itemVariants} className="button-group">
          <motion.button
            type="submit"
            onClick={handleSubmit}
            className={`add-button ${
              isLoading ||
              isPatientIncomplete() ||
              Object.values(patientErrors).some((error) => error)
                ? "disabled"
                : ""
            }`}
            disabled={
              isLoading ||
              isPatientIncomplete() ||
              Object.values(patientErrors).some((error) => error)
            }
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            {isLoading ? "Adding..." : "Add patient"}
          </motion.button>
          <motion.button
            type="button"
            onClick={() => navigate(-1)}
            className="cancel-button"
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            Cancel
          </motion.button>
        </motion.div>
      </motion.div>

      {/* Success Modal */}
      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        withCloseButton={false}
        centered
        size="sm"
        className="success-modal"
        overlayProps={{
          opacity: 0.55,
          blur: 3,
        }}
      >
        <div className="modal-content">
          <img
            src={`${process.env.PUBLIC_URL}/assets/logo.svg`}
            alt="Direct by Syndicate Bio"
            className="modal-logo"
          />
          <div className="modal-message-box">
            <Text className="modal-title">Patient added!</Text>
            <Text className="modal-message">
              Patient has been successfully added. You can now manage patient
              and order tests.
            </Text>
            <Button
              onClick={() => navigate(-1)}
              className="modal-close-button"
              style={{height: "fit-content",}}
            >
              Go Back
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AddPatient;